import type { Epic } from '../../index.js'
import { filter, mergeMap, of, EMPTY } from 'rxjs'
import { receivedWebSocketMessage } from '../../Slice/Application/ApplicationSlice.js'
import type { ItemChanged, WebsocketError, WebsocketMessage } from './Messages.js'
import { Actions } from '../../Slice/index.js'
import { orderSlice } from '../../Slice/Order/OrderSlice.js'
import { projectSlice } from '../../Slice/Project/ProjectSlice.js'
import { billingSlice } from '../../Slice/Billing/BillingSlice.js'

export const handleMessagesEpic: Epic = (action$, _, { OrderRepository }) =>
  action$.pipe(
    filter(receivedWebSocketMessage.match),
    mergeMap((action) => {
      const payload = action.payload

      if (!isWebsocketMessage(payload)) {
        console.warn('Unexpected websocket message: ', action.payload)
        return EMPTY
      }

      switch (payload.type) {
        case 'order-changed':
          return of(Actions[orderSlice.name].wsOrderChanged(payload))
        case 'item-changed':
          /* backend doesn't send the actual changed pool item for performance reasons */
          return OrderRepository.fetchPoolItem({
            orderId: (action.payload as ItemChanged).orderId,
            poolId: (action.payload as ItemChanged).poolId,
            itemId: (action.payload as ItemChanged).itemId
          }).pipe(
            mergeMap((res) =>
              of(
                Actions[orderSlice.name].wsItemChanged({
                  orderId: (action.payload as ItemChanged).orderId,
                  poolId: (action.payload as ItemChanged).poolId,
                  item: res
                })
              )
            )
          )
        case 'pipeline-data-changed':
          return of(Actions[orderSlice.name].wsPipelineDataChanged(payload))
        case 'pipeline-state-changed':
          return of(Actions[orderSlice.name].fetchPipelineState({ orderId: payload.orderId }))
        case 'pools-changed':
          return of(Actions[orderSlice.name].wsPoolsChanged(payload))
        case 'project-lock-changed':
          return of(
            Actions[projectSlice.name].wsProjectLockChanged({
              projectId: payload.projectId,
              causer: payload.causer,
              locked: payload.locked
            })
          )
        case 'project-samples-changed':
          /* S2-879: another collaborator changed the samples — refetch the project's sample list. */
          return of(Actions[projectSlice.name].getProjectSamplesRequest({ projectId: payload.projectId }))
        case 'project-updated':
          /* S2-879: another collaborator changed project settings/mode — refetch the project. */
          return of(Actions[projectSlice.name].getProjectRequest({ projectId: payload.projectId }))
        case 'billing-state-changed':
          console.info('billing state changed', payload)
          return EMPTY
        case 'credit-transactions-changed':
          /* S2-955: refresh the credit balance + history in the billing slice; every view that
             reads it (billing page, postprocessing/submit modals) updates from this one fetch. */
          return of(Actions[billingSlice.name].reloadCredits())
        case 'error':
          return handleWebsocketError(payload)
        case 'heartbeat':
          return EMPTY
        default:
          console.warn('Unhandled WebSocket message type: ', payload)
          return EMPTY
      }
    })
  )

/** ✅ Type guard to check if payload is a valid WebsocketMessage */
function isWebsocketMessage(payload: unknown): payload is WebsocketMessage {
  return typeof payload === 'object' && payload !== null && 'type' in payload
}

function handleWebsocketError(payload: WebsocketError) {
  console.error('WebSocket Error:', payload.message)
  return EMPTY
}
